import {
    signalStore,
    withState,
    withComputed,
    withMethods,
    patchState,
} from '@ngrx/signals';
import { CarrierDeliveryNoteInPool } from 'api/models/carrier-delivery-note-in-pool';
import { CarrierDeliveryNoteBundle } from 'api/models/carrier-delivery-note-bundle';
import { CarrierDeliveryNoteBundleOverview } from 'api/models/carrier-delivery-note-bundle-overview';

// ---------- State interface ----------
export interface TourPlanState {
    deliveryNotesPool: CarrierDeliveryNoteInPool[];
    tourPlanDeliveryNotes: CarrierDeliveryNoteInPool[];
    tourPlanBundles: CarrierDeliveryNoteBundle[];
    tourPlanBundleOverviews: CarrierDeliveryNoteBundleOverview[];
    isEditMode: boolean;
    tourEditKey: string;
    tourEditExternalName: string;
    tourPlanVersion: number;
}

// ---------- Initial state ----------
export const initialTourPlanState: TourPlanState = {
    deliveryNotesPool: [],
    tourPlanDeliveryNotes: [],
    tourPlanBundles: [],
    tourPlanBundleOverviews: [],
    isEditMode: false,
    tourEditKey: '',
    tourEditExternalName: '',
    tourPlanVersion: 0,
};

// ---------- NgRx Signal Store ----------
export const TourPlanStore = signalStore(
    { providedIn: 'root' },
    withState<TourPlanState>({ ...initialTourPlanState }),
    withComputed(() => ({
        // All state fields are exposed as signals by withState.
        // No additional derived computeds needed.
    })),
    withMethods(store => ({
        /** Replace the delivery notes pool. */
        setDeliveryNotesPool(pool: CarrierDeliveryNoteInPool[]): void {
            patchState(store, { deliveryNotesPool: pool });
        },

        /**
         * Add delivery notes to the tour plan, filtering out duplicates by `_key`.
         * Increments version when new notes are actually added.
         * @returns `true` if any notes were added, `false` otherwise.
         */
        addTourPlanDeliveryNotes(notes: CarrierDeliveryNoteInPool[]): boolean {
            const existingKeys = new Set(
                store.tourPlanDeliveryNotes().map(n => n._key)
            );
            const newNotes = notes.filter(n => !existingKeys.has(n._key));

            if (newNotes.length === 0) {
                return false;
            }

            patchState(store, state => ({
                tourPlanDeliveryNotes: [
                    ...state.tourPlanDeliveryNotes,
                    ...newNotes,
                ],
                tourPlanVersion: state.tourPlanVersion + 1,
            }));
            return true;
        },

        /**
         * Remove delivery notes from the tour plan.
         * Also removes any bundles whose delivery notes overlap with the removed set,
         * and restores the corresponding bundle overviews.
         * Increments version.
         * @returns the bundle overviews that were restored (removed from plan).
         */
        removeTourPlanDeliveryNotes(
            notes: CarrierDeliveryNoteInPool[]
        ): CarrierDeliveryNoteBundleOverview[] {
            const removedKeys = new Set(notes.map(n => n._key));

            // Find bundles that contain any of the removed notes
            const currentBundles = store.tourPlanBundles();
            const bundlesToRemove = currentBundles.filter(bundle =>
                bundle.deliveryNotes.some(dn => removedKeys.has(dn._key))
            );
            const bundleKeysToRemove = new Set(
                bundlesToRemove.map(b => b._key)
            );

            // Extract overviews that correspond to removed bundles
            const restoredOverviews = store.tourPlanBundleOverviews().filter(o =>
                bundleKeysToRemove.has(o._key)
            );

            patchState(store, state => ({
                tourPlanDeliveryNotes: state.tourPlanDeliveryNotes.filter(
                    n => !removedKeys.has(n._key)
                ),
                tourPlanBundles: state.tourPlanBundles.filter(
                    b => !bundleKeysToRemove.has(b._key)
                ),
                tourPlanBundleOverviews: state.tourPlanBundleOverviews.filter(
                    o => !bundleKeysToRemove.has(o._key)
                ),
                tourPlanVersion: state.tourPlanVersion + 1,
            }));

            return restoredOverviews;
        },

        /**
         * Add bundles and their overviews to the tour plan.
         * Increments version.
         */
        addTourPlanBundles(
            bundles: CarrierDeliveryNoteBundle[],
            overviews: CarrierDeliveryNoteBundleOverview[]
        ): void {
            patchState(store, state => ({
                tourPlanBundles: [...state.tourPlanBundles, ...bundles],
                tourPlanBundleOverviews: [
                    ...state.tourPlanBundleOverviews,
                    ...overviews,
                ],
                tourPlanVersion: state.tourPlanVersion + 1,
            }));
        },

        /**
         * Set edit mode state.
         */
        setEditMode(
            tourEditKey: string,
            tourEditExternalName: string
        ): void {
            patchState(store, {
                isEditMode: true,
                tourEditKey,
                tourEditExternalName,
            });
        },

        /**
         * Clear the tour plan — resets all state except the pool.
         * Increments version.
         */
        clearTourPlan(): void {
            patchState(store, state => ({
                tourPlanDeliveryNotes: [],
                tourPlanBundles: [],
                tourPlanBundleOverviews: [],
                isEditMode: false,
                tourEditKey: '',
                tourEditExternalName: '',
                tourPlanVersion: state.tourPlanVersion + 1,
            }));
        },

        /**
         * Full reset to initial state (including pool).
         */
        resetTourPlanStore(): void {
            patchState(store, { ...initialTourPlanState });
        },
    }))
);

